import { useEffect, useRef } from "react";

import { hasOpenLayer } from "../layers";

/**
 * Page-level single-key shortcuts (`j`, `k`, `s`, `a`, `/`, `?`, `Enter` …).
 *
 * Keys are the literal `event.key`, matched case-insensitively for letters.
 * A shortcut never fires when it could fight something else:
 * - the target is editable (input, textarea, select, contenteditable, textbox);
 * - Ctrl / Meta / Alt is held, or Shift with a letter (Shift+A never approves);
 * - an IME composition is running or another handler already took the event;
 * - an overlay (modal, drawer, menu) is open — it owns the keyboard.
 * A handled key calls `preventDefault` (stops Firefox quick-find on `/`).
 *
 * The map is read through a ref, so handlers always see current state.
 */
export type ShortcutMap = Record<string, (e: KeyboardEvent) => void>;

export function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  if (t.getAttribute("role") === "textbox") return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function useKeyboardShortcuts(map: ShortcutMap, { enabled = true }: { enabled?: boolean } = {}): void {
  const mapRef = useRef(map);
  useEffect(() => {
    mapRef.current = map;
  });

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.shiftKey && /^[a-z]$/i.test(e.key)) return;
      if (isEditableTarget(e.target) || hasOpenLayer()) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const handler = mapRef.current[key] ?? mapRef.current[e.key];
      if (!handler) return;
      e.preventDefault();
      handler(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
